import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, HardDriveDownload } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useAuth } from '../auth/AuthProvider';
import { listStreams, streamsKey, type Device, type DeviceStream } from '../inventory/api';
import { noProblem, toProblem, type Problem } from '../inventory/problem';
import {
  getRecording,
  putRecording,
  recordingErrorLabels,
  recordingKey,
  recordingPausedHints,
  recordingView,
  minutesPerDay,
  splitRetention,
  validHoursRetention,
  type EventSettings,
  type RecordingMode,
  type RecordingPolicy,
  type RecordingWindow,
  type RetentionUnit,
  defaultEvent,
  defaultWindow,
} from './api';
import { EventEditor } from './EventEditor';
import { RetentionField } from './RetentionField';
import { ScheduleEditor } from './ScheduleEditor';

interface Props {
  device: Device;
  canManage: boolean;
}

/** Continuous-recording policy and live state of one device (ADR-012). */
export function RecordingSection({ device, canManage }: Props) {
  const { request } = useAuth();
  const policy = useQuery({
    queryKey: recordingKey(device.id),
    queryFn: () => getRecording(request, device.id),
    // The supervisor changes the state on its own: keep it fresh while open.
    refetchInterval: 10_000,
  });
  const streams = useQuery({
    queryKey: streamsKey(device.id),
    queryFn: () => listStreams(request, device.id),
  });

  return (
    <section aria-labelledby="rec-heading" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3
            id="rec-heading"
            className="text-2xs font-medium tracking-wide text-fg-subtle uppercase"
          >
            Gravação
          </h3>
          <p className="mt-1 text-xs text-fg-muted">
            Grava sempre ou por agenda, em segmentos de 1 minuto apagados ao fim da retenção.
          </p>
        </div>
        {policy.data && <State policy={policy.data} />}
      </div>

      {policy.isPending && (
        <div aria-hidden="true" className="h-16 animate-pulse rounded-md bg-surface-hover" />
      )}
      {policy.isError && (
        <p role="alert" className="text-[13px] text-crit">
          Não foi possível carregar a gravação deste dispositivo.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => {
              void policy.refetch();
            }}
          >
            Tentar novamente
          </button>
        </p>
      )}
      {policy.data && (
        <PolicyForm
          // Remount when the saved configuration changes, so the form starts from it.
          key={`${String(policy.data.enabled)}|${policy.data.profile_token}|${String(policy.data.retention_minutes)}|${policy.data.mode}|${JSON.stringify(policy.data.schedule)}|${JSON.stringify(policy.data.event)}`}
          device={device}
          policy={policy.data}
          streams={streams.data ?? []}
          canManage={canManage}
        />
      )}
    </section>
  );
}

function State({ policy }: { policy: RecordingPolicy }) {
  const fmt = useTimeFormat();
  const v = recordingView(policy);
  return (
    <div className="text-right">
      <span title={policy.enabled ? recordingPausedHints[policy.state] : undefined}>
        <StatusBadge tone={v.tone} pulse={policy.enabled && policy.state === 'recording'}>
          {v.label}
        </StatusBadge>
      </span>
      {policy.enabled && policy.state_since && (
        <div className="mt-0.5 text-2xs text-fg-subtle">
          desde {fmt.relative(policy.state_since)}
        </div>
      )}
    </div>
  );
}

function PolicyForm({
  device,
  policy,
  streams,
  canManage,
}: {
  device: Device;
  policy: RecordingPolicy;
  streams: DeviceStream[];
  canManage: boolean;
}) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(policy.enabled);
  const [profile, setProfile] = useState(policy.profile_token);
  const savedRetention = splitRetention(policy.retention_minutes);
  const [unit, setUnit] = useState<RetentionUnit>(savedRetention.unit);
  const [days, setDays] = useState(String(savedRetention.days));
  const [hours, setHours] = useState(String(savedRetention.hours));
  const [minutes, setMinutes] = useState(String(savedRetention.minutes));
  const retention =
    unit === 'days' ? Number(days) * minutesPerDay : Number(hours) * 60 + Number(minutes);
  // A legacy value is shown as it is and only refused once the user changes it.
  const retentionInvalid =
    unit === 'hours' && !validHoursRetention(retention) && retention !== policy.retention_minutes;
  const [mode, setMode] = useState<RecordingMode>(policy.mode);
  const savedEvent = policy.event ?? defaultEvent;
  const [event, setEvent] = useState<EventSettings>(savedEvent);
  const [schedule, setSchedule] = useState<RecordingWindow[]>(
    policy.schedule.length > 0 ? policy.schedule : [defaultWindow],
  );
  const [problem, setProblem] = useState<Problem>(noProblem);
  const readOnly = !canManage || !policy.available;

  const dirty =
    enabled !== policy.enabled ||
    profile !== policy.profile_token ||
    retention !== policy.retention_minutes ||
    mode !== policy.mode ||
    (mode === 'schedule' && JSON.stringify(schedule) !== JSON.stringify(policy.schedule)) ||
    (mode === 'event' && JSON.stringify(event) !== JSON.stringify(savedEvent));

  // Every window needs days and both times, an event needs kinds and hours
  // need 5 min to 24 h; the API would refuse otherwise.
  const incomplete =
    retentionInvalid ||
    (mode === 'schedule' &&
      (schedule.length === 0 || schedule.some((w) => w.days.length === 0 || !w.start || !w.end))) ||
    (mode === 'event' && event.kinds.length === 0);

  const save = useMutation({
    mutationFn: () =>
      putRecording(request, device.id, {
        enabled,
        profile_token: profile,
        retention_minutes: retention,
        mode,
        schedule: mode === 'schedule' ? schedule : [],
        event,
      }),
    onSuccess: (p) => {
      qc.setQueryData(recordingKey(device.id), p);
      notify(
        !p.enabled
          ? 'Gravação desligada.'
          : p.mode === 'schedule'
            ? 'Gravação por agenda ligada.'
            : p.mode === 'event'
              ? 'Gravação por evento ligada.'
              : 'Gravação ligada.',
      );
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    save.mutate();
  };

  const usable = streams.filter((s) => s.uri);
  const main = usable[0];
  const known = profile === '' || usable.some((s) => s.profile_token === profile);

  return (
    <form onSubmit={submit} noValidate className="space-y-3">
      {!policy.available && (
        <Notice>
          Esta instalação não tem armazenamento de gravação configurado (RECORDING_DIR). As
          configurações podem ser vistas, mas nada é gravado.
        </Notice>
      )}
      {policy.enabled && policy.state === 'error' && policy.last_error && (
        <Notice>{recordingErrorLabels[policy.last_error]}</Notice>
      )}
      {enabled && usable.length === 0 && (
        <Notice>
          Nenhum perfil de vídeo conhecido. Execute o teste de conexão para que a gravação possa
          começar.
        </Notice>
      )}
      <FormProblem message={problem.form} />

      <label className="flex items-center gap-2.5 text-[13px] text-fg">
        <input
          type="checkbox"
          disabled={readOnly}
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
          }}
          className="size-4 accent-[var(--color-accent-strong)]"
        />
        Gravação ligada
      </label>

      <div
        role="radiogroup"
        aria-label="Quando gravar"
        className="flex flex-wrap gap-x-5 gap-y-1.5"
      >
        {(
          [
            ['continuous', 'Sempre (contínua)'],
            ['schedule', 'Por agenda'],
            ['event', 'Por evento'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 text-[13px] text-fg">
            <input
              type="radio"
              name={`rec-mode-${device.id}`}
              disabled={readOnly}
              checked={mode === value}
              onChange={() => {
                setMode(value);
              }}
              className="size-4 accent-[var(--color-accent-strong)]"
            />
            {label}
          </label>
        ))}
      </div>
      {mode === 'event' && (
        <EventEditor
          value={event}
          onChange={setEvent}
          readOnly={readOnly}
          errors={problem.fields}
        />
      )}
      {mode === 'schedule' && (
        <ScheduleEditor
          windows={schedule}
          onChange={setSchedule}
          readOnly={readOnly}
          timeZone={policy.time_zone}
          errors={problem.fields}
        />
      )}

      <div className="grid grid-cols-[1fr_13rem] gap-3">
        <Field
          label="Perfil gravado"
          error={problem.fields.profile_token}
          hint="O principal tem a melhor qualidade e ocupa mais espaço."
        >
          {({ id, describedBy }) => (
            <select
              id={id}
              disabled={readOnly}
              value={profile}
              onChange={(e) => {
                setProfile(e.target.value);
              }}
              aria-describedby={describedBy}
              className={inputClass}
            >
              <option value="">
                Principal{main ? ` (${main.name || main.profile_token})` : ''}
              </option>
              {usable.slice(1).map((s) => (
                <option key={s.profile_token} value={s.profile_token}>
                  {s.name || s.profile_token}
                  {s.width && s.height ? ` · ${String(s.width)}×${String(s.height)}` : ''}
                </option>
              ))}
              {!known && <option value={profile}>{profile} (não existe mais)</option>}
            </select>
          )}
        </Field>
        <RetentionField
          unit={unit}
          days={days}
          hours={hours}
          minutes={minutes}
          onUnit={setUnit}
          onDays={setDays}
          onHours={setHours}
          onMinutes={setMinutes}
          readOnly={readOnly}
          error={retentionInvalid ? 'De 5 min a 24 h' : problem.fields.retention}
        />
      </div>

      {canManage && policy.available && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-2xs text-fg-subtle">
            Mudar a retenção recalcula quando os trechos já gravados serão apagados.
          </p>
          <Button
            type="submit"
            className="shrink-0 whitespace-nowrap"
            disabled={!dirty || incomplete || save.isPending}
          >
            <HardDriveDownload aria-hidden="true" className="size-4" strokeWidth={1.75} />
            {save.isPending ? 'Salvando…' : 'Salvar gravação'}
          </Button>
        </div>
      )}
    </form>
  );
}

function Notice({ children }: { children: string }) {
  return (
    <p className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-[13px] text-fg">
      <CircleAlert
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-warn"
        strokeWidth={2}
      />
      {children}
    </p>
  );
}
